import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';

import { chatAPI } from 'services/api';
import { RootState } from 'store/rootReducer';

enum WppStatusEnum {
  APPROVED = 'APPROVED',
  PENDING = 'PENDING',
  REJECTED = 'REJECTED',
}

export enum FormatosDeCabecalhoMensagemTemplateEnum {
  TEXTO = 'TEXT',
  IMAGEM = 'IMAGE',
  VIDEO = 'VIDEO',
  DOCUMENTO = 'DOCUMENT',
}

export const MessageHeaderFormatsTemplate = {
  IMAGEM: 'IMAGE',
  VIDEO: 'VIDEO',
  DOCUMENTO: 'DOCUMENT',
} as const;

export type MessageHeaderFormatsTemplate =
  (typeof MessageHeaderFormatsTemplate)[keyof typeof MessageHeaderFormatsTemplate];

export enum WppCategoryEnum {
  AUTHENTICATION = 'AUTHENTICATION',
  MARKETING = 'MARKETING',
  UTILITY = 'UTILITY',
}

export const WppCategoryEnumLabels: Record<WppCategoryEnum, string> = {
  [WppCategoryEnum.AUTHENTICATION]: 'AUTENTICAÇÃO',
  [WppCategoryEnum.MARKETING]: 'MARKETING',
  [WppCategoryEnum.UTILITY]: 'UTILITÁRIO',
};

export type FormFieldVariablesTemplate = {
  name?: string;
  value?: string;
  values?: string[];
};

export type FormFieldHeaderTemplate = {
  type?: MessageHeaderFormatsTemplate;
  url?: string;
  storage_path?: string;
  storage_id?: string;
  filename?: string;
  variables: FormFieldVariablesTemplate[];
};

export type FormFieldTemplate = {
  header?: FormFieldHeaderTemplate;
  body?: {
    variables: FormFieldVariablesTemplate[];
  };
};

export type WppButtonsType =
  | 'URL'
  | 'PHONE_NUMBER'
  | 'COPY_CODE'
  | 'QUICK_REPLY';

export enum WppButtonsTypeEnum {
  URL = 'URL',
  PHONE_NUMBER = 'PHONE_NUMBER',
  COPY_CODE = 'COPY_CODE',
  QUICK_REPLY = 'QUICK_REPLY',
}

export type WppButtonType = {
  url?: string;
  text?: string;
  type: WppButtonsType;
  example?: string;
};

export type WppExampleType = {
  header_handle?: string[];
};

export type WppComponentsType = {
  text?: string;
  type?: 'HEADER' | 'BODY' | 'FOOTER' | 'BUTTONS';
  format?: FormatosDeCabecalhoMensagemTemplateEnum;
  buttons?: WppButtonType[];
  example?: WppExampleType;
};

export declare interface TemplateInterface {
  id: string;
  createdAt: string;
  whatsappIntegrationId: string;
  syncAt: string;
  wppName: string;
  wppMessageSendTtlSeconds: number;
  wppParameterFormat: string;
  wppLanguage: string;
  wppStatus: WppStatusEnum;
  wppCategory: WppCategoryEnum;
  wppSubCategory: string;
  wppComponents: WppComponentsType[];
  canInitiateAtendimento?: boolean;
  formFields?: FormFieldTemplate | null;
}

export interface OrderDetails {
  referenceId?: string;
  priceInCents?: number;
  pixDynamicCode?: {
    key?: string;
    code?: string;
    keyType?: string;
    merchantName?: string;
  };
}

export type WppTemplateData = {
  id: string;
  name: string;
  language: string;
  category?: WppCategoryEnum;
  subcategory?: string;
  components: WppComponentsType[];
  data?: {
    orderDetails?: OrderDetails;
  };
};

export interface TemplateMediaUploadResponse {
  url: string;
  storageId: string;
}

type TemplateState = {
  templates: TemplateInterface[];
  templateSelecionado?: TemplateInterface;
  loadingTemplates: boolean;
  error: string | null;
  status: WppStatusEnum;
  canInitiateAtendimento: boolean;
  category: WppCategoryEnum | null;
};

const initialState: TemplateState = {
  templates: [],
  loadingTemplates: false,
  templateSelecionado: undefined,
  error: null,
  status: WppStatusEnum.APPROVED,
  canInitiateAtendimento: true,
  category: null,
};

export const fetchTemplates = createAsyncThunk(
  'templates/fetchWhatsappTemplates',
  async (channelId: string, { getState }) => {
    const state = getState() as { templates: TemplateState };
    const { status, canInitiateAtendimento, category } = state.templates;

    const params: {
      status: WppStatusEnum;
      canInitiateAtendimento: boolean;
      channelId: string;
      category?: WppCategoryEnum;
    } = {
      status,
      canInitiateAtendimento,
      channelId,
    };

    if (category) {
      params.category = category;
    }

    const response = await chatAPI.get('v2/whatsapp/template-messages/select', {
      params,
    });

    return response.data as TemplateInterface[];
  },
);

export const uploadTemplateImage = createAsyncThunk<
  TemplateMediaUploadResponse,
  File
>('templates/uploadImage', async file => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await chatAPI.post(
    '/v2/whatsapp/template-messages/media/image',
    formData,
  );

  return response.data;
});

export const uploadTemplateVideo = createAsyncThunk<
  TemplateMediaUploadResponse,
  File
>('templates/uploadVideo', async file => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await chatAPI.post(
    '/v2/whatsapp/template-messages/media/video',
    formData,
  );

  return response.data;
});

export const uploadTemplateDocument = createAsyncThunk<
  TemplateMediaUploadResponse,
  File
>('templates/uploadDocument', async file => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await chatAPI.post(
    '/v2/whatsapp/template-messages/media/document',
    formData,
  );

  return response.data;
});

export const templatesSlice = createSlice({
  name: 'templates',
  initialState,
  reducers: {
    setTemplateSelecionado(
      state,
      action: PayloadAction<TemplateInterface | undefined>,
    ) {
      state.templateSelecionado = action.payload;
    },
    setTemplateCategory(state, action: PayloadAction<WppCategoryEnum | null>) {
      state.category = action.payload;
    },
  },
  extraReducers(builder) {
    builder.addCase(fetchTemplates.pending, state => {
      state.loadingTemplates = true;
      state.error = null;
    });
    builder.addCase(fetchTemplates.fulfilled, (state, action) => {
      state.loadingTemplates = false;
      state.templates = action.payload;
    });
    builder.addCase(fetchTemplates.rejected, (state, action) => {
      state.loadingTemplates = false;
      state.error = action.error.message || 'Erro ao buscar templates';
    });
  },
});

export const { setTemplateSelecionado, setTemplateCategory } =
  templatesSlice.actions;
export const templatesSelector = (state: RootState) => state.templates;
export default templatesSlice.reducer;
